// External Imports
import { createSelector } from 'reselect';

// Internal Imports
import { CONTENT_ITEM_SINGLE } from '../config/types/path.js';
import { pageType } from '../utils/tracking.js';
import { createGetContentItemBySlug } from './createGetContentItem.js';
import { getIsInProductMagazine } from './getIsInProductMagazine.js';
import { getLocale } from './getLocale.js';
import { getLearnLanguage } from './getLearnLanguage.js';
import { getArticleSlug, getContentType, getPathType, getUrl, getQuery } from './getMatch.js';
import { selectUUID, selectIsInitialized } from './user.js';

// Local Functions
/**
 * Selector that will return the payload for the event tracking.
 *
 * @param {string} state of all
 * @param {string} uuid of the user
 * @param {string} isUserLoading check if the request is done
 * @param {string} pathType type of the page
 * @param {string} locale Magazine language edition
 * @param {string} url path
 * @param {string} query query params for the url
 * @param {string} contentType data of the article
 * @param {string} articleSlug slug of the article
 * @return {function}   selector function
 */
const getPageTrackingData = createSelector(
  [
    (state) => state,
    selectUUID,
    selectIsInitialized,
    getPathType,
    getLocale,
    getUrl,
    getQuery,
    getContentType,
    getArticleSlug,
    getLearnLanguage,
    getIsInProductMagazine,
  ],
  (
    state,
    uuid,
    isUserInitialized,
    pathType,
    locale,
    url,
    query,
    contentType,
    articleSlug,
    learnLanguage,
    isInProductMagazine,
  ) => {
    let articlePayload = {};
    const search = Object.entries(query)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');

    if (!isUserInitialized) {
      return { data: {}, isLoading: true };
    }

    if (pathType === CONTENT_ITEM_SINGLE) {
      const getItem = createGetContentItemBySlug(contentType);
      const item = getItem(state);
      if (!item) {
        return { data: {}, isLoading: true };
      }
      const {
        metadata: { categories: [category] = [], coauthors: [author] = [], tags },
      } = item;
      articlePayload = {
        ...articlePayload,
        ...(author?.slug && { article_author: author.slug }),
        ...(category && { article_category: category }),
        ...(tags?.length && { article_tags: tags.map(({ label }) => label) }),
        article_slug: articleSlug,
      };
    }

    return {
      data: {
        ...articlePayload,
        locale: locale === 'en-US' ? 'en' : locale,
        page_path: `${url}${search.length > 0 ? `?${search}` : ''}`,
        page_type: pageType[pathType],
        ...(uuid && { uuid }),
        in_product: isInProductMagazine,
        learn_language_alpha3: learnLanguage,
      },
      isLoading: false,
    };
  },
);

// Module Exports
export { getPageTrackingData };
